import { useState } from 'react';
import type { Card, MoxfieldUserDeck } from '@mtg-deck-tool/shared';
import { api, ApiClientError } from '../../lib/api-client.js';
import { useCollectionStore } from '../../stores/collection-store.js';
import { ManaSymbolIcons } from '../shared/ManaSymbolIcons.js';

interface MoxfieldImportFlowProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface PendingDeckImport {
  id: string;
  url: string;
  name: string;
  commanderName: string;
  commanderCard: Card | null;
  commanderCropUrl?: string | null;
  colors: string[];
  cardCount: number;
  isSelected: boolean;
  status: 'pending' | 'resolving' | 'resolved' | 'failed' | 'importing' | 'imported';
  error?: string;
}

function parseMoxfieldDeckId(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;
  // If it's just a alphanumeric ID of 8+ characters without slashes, it might be the ID directly
  if (/^[A-Za-z0-9_-]{8,}$/.test(trimmed)) return trimmed;
  const match = /moxfield\.com\/decks\/([A-Za-z0-9_-]+)/i.exec(trimmed);
  return match?.[1] ?? null;
}

function parseMoxfieldUsername(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  
  // Match URL like moxfield.com/users/username
  const urlMatch = /moxfield\.com\/users\/([A-Za-z0-9_-]+)/i.exec(trimmed);
  if (urlMatch) return urlMatch[1] ?? null;
  
  // Match @username
  if (trimmed.startsWith('@')) {
    return trimmed.substring(1).trim();
  }
  
  return null;
}

export function MoxfieldImportFlow({ onClose, onSuccess }: MoxfieldImportFlowProps) {
  const [urlsInput, setUrlsInput] = useState('');
  const [decks, setDecks] = useState<PendingDeckImport[]>([]);
  const [isResolving, setIsResolving] = useState(false);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number; deckName: string } | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [isImportDone, setIsImportDone] = useState(false);

  const fetchCollection = useCollectionStore((s) => s.fetchCollection);

  // Phase 1: Resolve inputted URLs in parallel to get deck preview metadata
  const handleResolveDecks = async () => {
    setGeneralError(null);
    const trimmedInput = urlsInput.trim();
    if (!trimmedInput) {
      setGeneralError('Please enter at least one Moxfield deck URL, ID, or a user profile.');
      return;
    }

    setIsResolving(true);

    // Check if the input is a Moxfield username profile URL or starts with @
    const username = parseMoxfieldUsername(trimmedInput);

    if (username) {
      try {
        const userDecks = await api.getMoxfieldUserDecks(username);
        
        if (userDecks.length === 0) {
          setGeneralError(`No public decks found for user "${username}".`);
          setIsResolving(false);
          return;
        }

        const pendingDecks: PendingDeckImport[] = userDecks.map((d: MoxfieldUserDeck) => ({
          id: d.publicId,
          url: `https://www.moxfield.com/decks/${d.publicId}`,
          name: d.name,
          commanderName: d.leadCardName,
          commanderCard: null,
          commanderCropUrl: d.leadCardCropUrl,
          colors: d.colors,
          cardCount: d.cardCount,
          isSelected: d.format.toLowerCase() === 'commander' || d.format.toLowerCase() === 'edh',
          status: 'resolved',
        }));

        setDecks(pendingDecks);
      } catch (err: any) {
        const errMsg = err instanceof ApiClientError ? err.message : 'Failed to fetch user decks';
        setGeneralError(`Error fetching decks for user "${username}": ${errMsg}`);
      } finally {
        setIsResolving(false);
      }
      return;
    }

    const lines = urlsInput.split('\n').map((l) => l.trim()).filter(Boolean);
    const pendingDecks: PendingDeckImport[] = [];

    for (const line of lines) {
      const id = parseMoxfieldDeckId(line);
      if (id) {
        // Prevent duplicate IDs in the same batch
        if (pendingDecks.some((d) => d.id === id)) continue;
        pendingDecks.push({
          id,
          url: line,
          name: `Deck (${id})`,
          commanderName: 'Resolving...',
          commanderCard: null,
          colors: [],
          cardCount: 0,
          isSelected: true,
          status: 'pending',
        });
      }
    }

    if (pendingDecks.length === 0) {
      setGeneralError('No valid Moxfield deck URLs, IDs, or user profiles were found.');
      setIsResolving(false);
      return;
    }

    setDecks(pendingDecks);

    // Parallel resolution of deck metadata
    await Promise.all(
      pendingDecks.map(async (deck, index) => {
        setDecks((prev) =>
          prev.map((d, i) => (i === index ? { ...d, status: 'resolving' } : d))
        );

        try {
          // Fetch raw deck details using resolve endpoint
          const resolved = await api.resolveMoxfieldDeck(deck.id);
          const commanders = resolved.deck.filter((e) => e.isCommander);
          const commanderName = commanders.map((e) => e.card.name).join(' & ');
          const commanderCard = commanders[0]?.card ?? null;
          
          // Compute color identity
          const uniqueColors = new Set<string>();
          for (const c of commanders) {
            for (const col of c.card.colorIdentity) {
              uniqueColors.add(col);
            }
          }
          const colors = Array.from(uniqueColors);
          const cardCount = resolved.deck.reduce((sum, e) => sum + e.quantity, 0);

          // Get deck name if possible, or derive from commanders
          const name = commanderName ? `${commanderName} Deck` : `Moxfield Deck (${deck.id})`;

          setDecks((prev) =>
            prev.map((d, i) =>
              i === index
                ? {
                    ...d,
                    name,
                    commanderName,
                    commanderCard,
                    colors,
                    cardCount,
                    status: 'resolved',
                  }
                : d
            )
          );
        } catch (err: any) {
          const errMsg = err instanceof ApiClientError ? err.message : 'Failed to resolve';
          setDecks((prev) =>
            prev.map((d, i) =>
              i === index
                ? {
                    ...d,
                    status: 'failed',
                    error: errMsg,
                    isSelected: false,
                  }
                : d
            )
          );
        }
      })
    );

    setIsResolving(false);
  };

  // Toggle selection for a deck
  const toggleSelectDeck = (id: string) => {
    setDecks((prev) =>
      prev.map((d) => (d.id === id && d.status === 'resolved' ? { ...d, isSelected: !d.isSelected } : d))
    );
  };

  // Toggle all resolved decks
  const toggleSelectAll = (checked: boolean) => {
    setDecks((prev) =>
      prev.map((d) => (d.status === 'resolved' ? { ...d, isSelected: checked } : d))
    );
  };

  // Phase 2: sequentially import checked decks
  const handleBulkImport = async () => {
    setGeneralError(null);
    setIsImportDone(false);
    const toImport = decks.filter((d) => d.isSelected && d.status === 'resolved');
    if (toImport.length === 0) {
      setGeneralError('Please select at least one deck to import.');
      return;
    }

    const total = toImport.length;
    let current = 0;

    for (const deck of toImport) {
      current++;
      setImportProgress({ current, total, deckName: deck.name });
      setDecks((prev) =>
        prev.map((d) => (d.id === deck.id ? { ...d, status: 'importing' } : d))
      );

      try {
        await api.importMoxfieldDeck(deck.id);
        setDecks((prev) =>
          prev.map((d) => (d.id === deck.id ? { ...d, status: 'imported' } : d))
        );
      } catch (err: any) {
        const errMsg = err instanceof ApiClientError ? err.message : 'Import failed';
        setDecks((prev) =>
          prev.map((d) => (d.id === deck.id ? { ...d, status: 'failed', error: errMsg } : d))
        );
      }
    }

    setImportProgress(null);
    await fetchCollection();
    setIsImportDone(true);
  };

  const hasPending = decks.length > 0;
  const resolvedCount = decks.filter((d) => d.status === 'resolved').length;
  const selectedCount = decks.filter((d) => d.isSelected && d.status === 'resolved').length;
  const allResolvedChecked = resolvedCount > 0 && selectedCount === resolvedCount;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={isImportDone ? onSuccess : onClose}>
      <div
        className="relative w-full max-w-4xl mx-4 bg-gray-900/90 border border-gray-800 rounded-2xl shadow-2xl flex flex-col max-h-[85vh] overflow-hidden backdrop-blur-md"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800 bg-gray-950/40">
          <div>
            <h2 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-300">
              Import Moxfield Decks
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Paste URLs or IDs to selectively bulk import decks into your collection
            </p>
          </div>
          <button
            onClick={isImportDone ? onSuccess : onClose}
            className="text-gray-400 hover:text-gray-200 transition-colors p-1.5 hover:bg-gray-800 rounded-lg"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content Container */}
        <div className="p-6 flex-1 overflow-y-auto min-h-0 space-y-5">
          {generalError && (
            <div className="p-4 bg-red-950/40 border border-red-900/50 text-red-300 text-sm rounded-xl flex items-center gap-2">
              <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {generalError}
            </div>
          )}

          {!hasPending ? (
            <div className="space-y-4">
              <label className="block text-sm font-semibold text-gray-300">
                Enter Moxfield Deck URLs/IDs (one per line) or a User Profile:
              </label>
              <textarea
                value={urlsInput}
                onChange={(e) => setUrlsInput(e.target.value)}
                placeholder="https://www.moxfield.com/users/Gebeater&#10;or @Gebeater&#10;or a list of deck URLs (one per line)"
                rows={8}
                className="w-full bg-gray-950/60 border border-gray-800 rounded-xl px-4 py-3 text-sm text-gray-100 placeholder-gray-600 font-mono focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/20 resize-none transition-all"
              />
              <div className="text-xs text-gray-500 space-y-1">
                <p>💡 Tip: Paste a user profile link (e.g. <code>moxfield.com/users/Gebeater</code>) or type <code>@username</code> to browse and selectively import their decks in bulk!</p>
                <p>🔒 Privacy note: Only public or unlisted Moxfield decks can be imported.</p>
              </div>
            </div>
          ) : (
            /* Deck Checklist Grid */
            <div className="space-y-4 min-h-0 flex flex-col">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <span className="font-semibold text-gray-200">{selectedCount}</span> of{' '}
                  <span className="font-semibold text-gray-200">{resolvedCount}</span> ready decks selected
                </div>

                {!isResolving && !importProgress && (
                  <button
                    onClick={() => {
                      setDecks([]);
                      setUrlsInput('');
                    }}
                    className="text-xs text-orange-400 hover:text-orange-300 font-medium transition-colors"
                  >
                    Reset & start over
                  </button>
                )}
              </div>

              {/* Table Wrapper */}
              <div className="border border-gray-800 rounded-xl overflow-hidden bg-gray-950/40 flex-1 min-h-0 flex flex-col">
                <div className="overflow-x-auto overflow-y-auto max-h-[55vh] min-h-[150px]">
                  <table className="w-full text-left border-collapse text-sm">
                    <thead className="bg-gray-950/80 sticky top-0 z-10 border-b border-gray-800 text-gray-400 text-[10px] uppercase font-semibold tracking-wider">
                      <tr>
                        <th className="py-2 px-3 w-10">
                          <input
                            type="checkbox"
                            checked={allResolvedChecked}
                            onChange={(e) => toggleSelectAll(e.target.checked)}
                            disabled={resolvedCount === 0 || !!importProgress}
                            className="rounded border-gray-800 bg-gray-900 text-orange-500 focus:ring-orange-500/30 w-3.5 h-3.5 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                          />
                        </th>
                        <th className="py-2 px-3">Commander / Deck</th>
                        <th className="py-2 px-3 w-32 text-center">Colors</th>
                        <th className="py-2 px-3 w-24 text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800/60">
                      {decks.map((deck) => {
                        const cropUri = deck.commanderCard?.imageUris?.artCrop ?? 
                                        deck.commanderCard?.cardFaces?.[0]?.imageUris?.artCrop ??
                                        deck.commanderCropUrl;

                        return (
                          <tr
                            key={deck.id}
                            className={`hover:bg-gray-800/10 transition-colors ${
                              deck.isSelected ? 'bg-orange-500/[0.01]' : 'opacity-70'
                            }`}
                          >
                            <td className="py-2 px-3 align-middle">
                              <input
                                type="checkbox"
                                checked={deck.isSelected}
                                onChange={() => toggleSelectDeck(deck.id)}
                                disabled={deck.status !== 'resolved' || !!importProgress}
                                className="rounded border-gray-800 bg-gray-900 text-orange-500 focus:ring-orange-500/30 w-3.5 h-3.5 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                              />
                            </td>
                            <td className="py-2 px-3 align-middle">
                              <div className="flex items-center gap-2.5">
                                {/* Commander Portrait Thumbnail */}
                                <div className="w-8 h-8 rounded-md overflow-hidden border border-gray-800 shrink-0 bg-gray-900 flex items-center justify-center">
                                  {cropUri ? (
                                    <img src={cropUri} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <span className="text-gray-700 text-xs">🃏</span>
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <div className="text-xs font-semibold text-gray-200 truncate max-w-[320px]" title={deck.name}>
                                    {deck.status === 'resolving' ? `Resolving ${deck.id}...` : deck.name}
                                  </div>
                                  <div className="text-[10px] text-gray-500 truncate max-w-[320px]" title={deck.commanderName}>
                                    {deck.commanderName || deck.id}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="py-2 px-3 align-middle text-center">
                              {deck.status === 'resolved' || deck.status === 'imported' || deck.status === 'importing' ? (
                                <div className="flex justify-center">
                                  <ManaSymbolIcons colors={deck.colors as any} size={13} />
                                </div>
                              ) : (
                                <span className="text-gray-600">—</span>
                              )}
                            </td>
                            <td className="py-2 px-3 align-middle text-right">
                              {deck.status === 'pending' && (
                                <span className="text-[10px] text-gray-500 bg-gray-800/40 px-2 py-0.5 rounded">Queued</span>
                              )}
                              {deck.status === 'resolving' && (
                                <span className="text-[10px] text-orange-400 bg-orange-950/20 border border-orange-900/30 px-2 py-0.5 rounded flex items-center gap-1 justify-end">
                                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-ping" />
                                  Resolving
                                </span>
                              )}
                              {deck.status === 'resolved' && (
                                <span className="text-[10px] text-green-400 bg-green-950/20 border border-green-900/30 px-2 py-0.5 rounded">Ready</span>
                              )}
                              {deck.status === 'importing' && (
                                <span className="text-[10px] text-orange-400 bg-orange-950/20 border border-orange-900/30 px-2 py-0.5 rounded flex items-center gap-1 justify-end">
                                  <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-bounce" />
                                  Importing
                                </span>
                              )}
                              {deck.status === 'imported' && (
                                <span className="text-[10px] text-emerald-400 bg-emerald-950/20 border border-emerald-900/30 px-2 py-0.5 rounded">Imported</span>
                              )}
                              {deck.status === 'failed' && (
                                <div className="flex flex-col items-end">
                                  <span className="text-[10px] text-red-400 bg-red-950/20 border border-red-900/30 px-2 py-0.5 rounded">Failed</span>
                                  <span className="text-[9px] text-red-500 mt-0.5 max-w-[150px] truncate" title={deck.error}>
                                    {deck.error}
                                  </span>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Import Progress Overlay */}
          {importProgress && (
            <div className="p-4 bg-gray-950/80 border border-orange-950 rounded-xl space-y-2">
              <div className="flex justify-between text-xs text-gray-400">
                <span>Importing: <strong className="text-orange-400">{importProgress.deckName}</strong></span>
                <span>{importProgress.current} of {importProgress.total}</span>
              </div>
              <div className="w-full bg-gray-800 rounded-full h-1.5 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-orange-500 to-amber-400 h-full rounded-full transition-all duration-300"
                  style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end items-center gap-3 px-6 py-4 border-t border-gray-800 bg-gray-950/40">
          <button
            onClick={isImportDone ? onSuccess : onClose}
            disabled={isResolving || !!importProgress}
            className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-40"
          >
            {isImportDone ? 'Close' : 'Cancel'}
          </button>

          {!hasPending ? (
            <button
              onClick={() => { void handleResolveDecks(); }}
              disabled={isResolving || !urlsInput.trim()}
              className="px-5 py-2 text-sm font-semibold bg-orange-600 hover:bg-orange-500 disabled:bg-orange-800 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl transition-all flex items-center gap-2"
            >
              {isResolving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Resolving...
                </>
              ) : (
                'Fetch Decks'
              )}
            </button>
          ) : (
            <button
              onClick={() => {
                if (isImportDone) {
                  onSuccess();
                } else {
                  void handleBulkImport();
                }
              }}
              disabled={isResolving || !!importProgress || (!isImportDone && selectedCount === 0)}
              className={`px-5 py-2 text-sm font-semibold text-white rounded-xl transition-all shadow-lg flex items-center gap-2 ${
                isImportDone
                  ? 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-500/10'
                  : 'bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 disabled:from-orange-850 disabled:to-amber-850 disabled:opacity-40 disabled:cursor-not-allowed shadow-orange-500/10'
              }`}
            >
              {importProgress ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Importing...
                </>
              ) : isImportDone ? (
                'Done'
              ) : (
                `Import Selected (${selectedCount})`
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
